import React, { FC, useState } from "react";

import { validateEmail } from "Utils";
import { cognitoForgotPassword, cognitoConfirmPassword } from "Utils/cognito";
import { validatePassword, checkPasswordRules } from "Utils/passwordValidation";
import {
  ForgotFormContainerStyled,
  TitleStyled,
  InputStyled,
  SubmitStyled,
  CancelStyled,
  ErrMsgStyled,
  FormDescriptionStyled,
  PasswordRequirementsStyled,
  RequirementItemStyled,
} from "./styles";

type ForgotPasswordProps = {
  onCancel: () => void;
};

type ForgotPasswordStep = "EMAIL" | "CODE" | "SUCCESS";

export const ForgotPasswordComponent: FC<ForgotPasswordProps> = ({
  onCancel,
}) => {
  const [step, setStep] = useState<ForgotPasswordStep>("EMAIL");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);

  // Step 1: Request verification code
  const handleRequestCode = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const currentEmail = email || "";

    setIsEmailInvalid(false);
    setError(null);

    if (!currentEmail || !validateEmail(currentEmail)) {
      setIsEmailInvalid(true);
      return;
    }

    setLoading(true);
    try {
      await cognitoForgotPassword(currentEmail);
      setStep("CODE");
    } catch (err: any) {
      // Don't reveal if user exists (security best practice)
      if (err.code === "UserNotFoundException") {
        setStep("CODE");
      } else if (err.code === "LimitExceededException") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.message || "Failed to send verification code");
      }
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Confirm new password with code
  const handleConfirmPassword = async (
    ev: React.FormEvent<HTMLFormElement>
  ) => {
    ev.preventDefault();
    setError(null);

    if (!code) {
      setError("Verification code is required");
      return;
    }

    if (!newPassword) {
      setError("New password is required");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setLoading(true);
    try {
      await cognitoConfirmPassword(email, code, newPassword);
      setStep("SUCCESS");
    } catch (err: any) {
      if (err.code === "CodeMismatchException") {
        setError("Invalid verification code");
      } else if (err.code === "ExpiredCodeException") {
        setError("Verification code has expired. Please request a new one.");
      } else if (err.code === "InvalidPasswordException") {
        setError(
          "Password does not meet requirements (min 12 characters, uppercase, lowercase, numbers, special characters)"
        );
      } else if (err.code === "LimitExceededException") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.message || "Failed to reset password");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToEmail = () => {
    setStep("EMAIL");
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
  };

  // Step 1: Email input
  if (step === "EMAIL") {
    return (
      <ForgotFormContainerStyled onSubmit={handleRequestCode}>
        <TitleStyled>Forgot Password</TitleStyled>
        {isEmailInvalid && <ErrMsgStyled>Valid email required.</ErrMsgStyled>}
        {error && <ErrMsgStyled>{error}</ErrMsgStyled>}
        <FormDescriptionStyled>
          Enter your email address to receive a verification code.
        </FormDescriptionStyled>
        <InputStyled
          type="email"
          name="email"
          onChange={(e) => setEmail(e.currentTarget.value.toLowerCase())}
          placeholder="Email"
          value={email}
          disabled={loading}
        />
        <SubmitStyled disabled={loading}>
          {loading ? "SENDING..." : "SEND CODE"}
        </SubmitStyled>
        <CancelStyled type="button" onClick={onCancel} disabled={loading}>
          CANCEL
        </CancelStyled>
      </ForgotFormContainerStyled>
    );
  }

  // Step 2: Code + new password input
  if (step === "CODE") {
    return (
      <ForgotFormContainerStyled onSubmit={handleConfirmPassword}>
        <TitleStyled>Reset Password</TitleStyled>
        {error && <ErrMsgStyled>{error}</ErrMsgStyled>}
        <FormDescriptionStyled>
          Enter the verification code sent to {email}
        </FormDescriptionStyled>
        <InputStyled
          type="text"
          name="code"
          placeholder="Verification Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={loading}
          autoComplete="off"
        />
        <InputStyled
          type="password"
          name="newPassword"
          placeholder="New Password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading}
        />
        <PasswordRequirementsStyled>
          <RequirementItemStyled met={checkPasswordRules(newPassword).length}>
            At least 12 characters
          </RequirementItemStyled>
          <RequirementItemStyled
            met={checkPasswordRules(newPassword).uppercase}
          >
            Uppercase letter
          </RequirementItemStyled>
          <RequirementItemStyled
            met={checkPasswordRules(newPassword).lowercase}
          >
            Lowercase letter
          </RequirementItemStyled>
          <RequirementItemStyled met={checkPasswordRules(newPassword).number}>
            Number
          </RequirementItemStyled>
          <RequirementItemStyled met={checkPasswordRules(newPassword).special}>
            Special character
          </RequirementItemStyled>
        </PasswordRequirementsStyled>
        <InputStyled
          type="password"
          name="confirmPassword"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
        />
        <ErrMsgStyled
          style={{
            visibility:
              confirmPassword && newPassword !== confirmPassword
                ? "visible"
                : "hidden",
          }}
        >
          Passwords do not match
        </ErrMsgStyled>
        <SubmitStyled disabled={loading}>
          {loading ? "RESETTING..." : "RESET PASSWORD"}
        </SubmitStyled>
        <CancelStyled
          type="button"
          onClick={handleBackToEmail}
          disabled={loading}
        >
          BACK
        </CancelStyled>
      </ForgotFormContainerStyled>
    );
  }

  // Step 3: Success
  return (
    <ForgotFormContainerStyled as="div">
      <TitleStyled>Password Reset</TitleStyled>
      <FormDescriptionStyled>
        Your password has been reset successfully.
      </FormDescriptionStyled>
      <SubmitStyled as="button" onClick={onCancel}>
        RETURN TO LOGIN
      </SubmitStyled>
    </ForgotFormContainerStyled>
  );
};
